import { KeyboardEvent, useCallback, useContext, useEffect } from "react";

import { useRouter } from "next/router";

import { useTranslator } from "@eo-locale/react";

import {
  clearAllErrors,
  resetAnswers
} from "@/redux/features/place-form/slice";
import {
  resetIsMarkerInsidePolygon,
  resetMarker,
  resetMarkerError,
  setPreviousMarker
} from "@/redux/features/place-marker/slice";
import {
  resetFilterCategories,
  setMarkers
} from "@/redux/features/place-markers/slice";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";
import {
  setPinSearchFromUrl,
  setView
} from "@/redux/features/place-right-panel/slice";
import { setPinDetails, updateMapBounds } from "@/redux/features/place/slice";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  Box,
  Button,
  FiArrowLeft,
  FiList,
  FiMapPin,
  IconButton,
  useBreakpointValue,
  useMediaQuery
} from "@/storybook-components";
import { EsriContext } from "../../context/esri-context";
import { EsriContextType } from "../../context/esri-context-type";
import FilterPinModal from "../filter-pin-modal";
import MapDetails from "../help";
import WrapperComponent from "../wrapper-component";
import MobileInteractions from "./mobile-interactions";

import useUser from "@/hooks/useUser";

import { setCurrentPageNumber } from "@/redux/features/place-marker/slice";
import AccessibilityKeysModal from "../keyboard-accessibility";
import UnsavedChangesModal from "../unsaved-changes-modal";

import useAccessStatus from "@/hooks/places/use-access-status";
import useEmbeds from "@/hooks/useEmbeds";

interface Props {
  activityRef: any;
  onOpen: () => void;
  id: string;
  handlePinClick: () => void;
  handleActivityFeedClick: () => void;
  setActionsVisibility: any;
  handleDropPinMobile: any;
  rightPanelProps: any; // close the activity feed drawer
}

const Interactions = (props: Props) => {
  const {
    activityRef,
    onOpen,
    handlePinClick,
    handleActivityFeedClick,
    handleDropPinMobile,
    rightPanelProps
  } = props;

  const [isMobile] = useMediaQuery("(max-width: 768px)");
  const { mapRef, setActionsVisibility } = useContext(
    EsriContext
  ) as EsriContextType;
  const { isEmbed } = useEmbeds();
  const router = useRouter();
  const { translate } = useTranslator();
  const { prevMarkers } = useAppSelector((state) => state.placesMarkerSlice);
  const projectId = useAppSelector((state) => state.project.project.id);
  const placesId = useAppSelector((state) => state.place.place.id);
  const { googleTranslationEnabled } = useAppSelector(
    (state) => state.settings.settings
  );
  const { isUnverfiedUserLoggedIn } = useUser();
  const centerCoordinates = useAppSelector(
    (state) => state.place.centerCoordinates
  );
  const dispatch = useAppDispatch();
  const { accessStatus } = useAccessStatus();
  const responsiveSize = useBreakpointValue(
    { base: "lg", lg: "md" },
    { ssr: false }
  );

  const activityFeedBtnProps = {
    size: responsiveSize,
    variant: "solid",
    borderRadius: "50%",
    bg: "white",
    color: "gray.900",
    boxShadow: responsiveSize,
    _hover: { background: "gray.100", color: "gray.900" },
    ref: activityRef,
    className: "activity-feed-button",
    "aria-label": translate("places.feed")
  };

  const rightPanelView = useAppSelector(
    (state) => state.placesRightPanelView.view
  );
  const { asPath } = router;
  const handleClick = useCallback(async () => {
    await dispatch(resetFilterCategories());
    const updatedPath = asPath.includes("/marker/")
      ? asPath?.split("/marker/")?.[0]
      : asPath?.split("?")?.[0];
    dispatch(setPinSearchFromUrl(false));
    router.replace(updatedPath, "", { shallow: true });

    if (isMobile) {
      dispatch(resetIsMarkerInsidePolygon());
      setActionsVisibility(false);
      dispatch(resetAnswers());
      dispatch(clearAllErrors());
      dispatch(resetMarker());
      dispatch(resetMarkerError());
      dispatch(setView("activity-feed"));
      dispatch(setPinDetails({}));
      onOpen();
    } else {
      await handleActivityFeedClick();
    }

    if (prevMarkers.length) {
      await dispatch(setMarkers(prevMarkers));

      dispatch(setPreviousMarker([]));
      const { _southWest, _northEast } = mapRef.getBounds();

      const bounds = {
        lat_min: _southWest.lat,
        lat_max: _northEast.lat,
        lng_min: _southWest.lng,
        lng_max: _northEast.lng
      };

      dispatch(updateMapBounds(bounds));
    }
    await dispatch(setCurrentPageNumber(1));
    await dispatch(
      fetchPinDetails({
        projectId,
        placesId,
        filters: {
          sort: { ["created_at"]: "desc" }
        }
      })
    );
  }, [
    isMobile,
    mapRef,
    rightPanelView,
    prevMarkers,
    setPreviousMarker,
    setCurrentPageNumber,
    prevMarkers,
    centerCoordinates
  ]);

  const navigateToProjectsPage = () => {
    if (isMobile && !isEmbed) {
      return (
        <Box pr={2}>
          <Box display={{ base: "auto", lg: "flex", sm: "flex" }}>
            <IconButton
              top={googleTranslationEnabled ? 12 : 0}
              size={responsiveSize}
              boxShadow={responsiveSize}
              variant="solid"
              borderRadius="50%"
              bg="white"
              color="gray.900"
              _hover={{ background: "gray.100", color: "gray.900" }}
              onClick={() => {
                router.push(`/${router.asPath.split("/")[1]}`);
              }}
              icon={<FiArrowLeft />}
              aria-label={translate("places.goBackToHome")}
            />
          </Box>
        </Box>
      );
    }
  };

  const iconComponent = useCallback(() => {
    const navbarElement = document.getElementsByClassName(
      "ehq-participant-header"
    )[0];

    const topMargin = googleTranslationEnabled ? "12.5rem" : "9.5rem";
    const top = navbarElement ? topMargin : "1rem";

    return (
      <>
        <WrapperComponent
          top={{ base: "0px", lg: `${top}`, sm: "1rem" }}
          position="absolute"
          left={{ base: "0px", lg: "1.25rem", sm: "1.25rem" }}
          zIndex={999}
        >
          <Box display="flex">
            {navigateToProjectsPage()}
            <Box pr={2}>
              <MapDetails rightPanelProps={rightPanelProps} />
            </Box>
          </Box>
        </WrapperComponent>
        <WrapperComponent
          top={{ base: "0px", lg: `${top}`, sm: "1rem" }}
          position="absolute"
          right={{ base: "0px", lg: "calc(30vw + 1.25rem)", sm: "0px" }}
          zIndex={999}
        >
          <Box display="flex">
            <Box display={{ base: "auto", lg: "flex", sm: "none" }} pr={4}>
              <IconButton
                icon={<FiList />}
                onClick={() => handleClick()}
                {...activityFeedBtnProps}
              />
            </Box>

            <Box display={{ base: "auto", lg: "flex", sm: "none" }} pr={4}>
              <FilterPinModal />
            </Box>

            {!isMobile ? (
              <Box display={{ base: "auto", lg: "flex", sm: "none" }} pr={4}>
                <AccessibilityKeysModal />
              </Box>
            ) : null}

            {accessStatus ? (
              <Box display={{ base: "auto", lg: "flex", sm: "none" }}>
                <Button
                  leftIcon={<FiMapPin />}
                  size="md"
                  boxShadow="md"
                  variant="primary"
                  onClick={handlePinClick}
                  aria-label={translate("places.pin.new")}
                >
                  {translate("places.pin.new")}
                </Button>
              </Box>
            ) : null}
          </Box>

          {isMobile && (
            <MobileInteractions
              handleDropPinMobile={handleDropPinMobile}
              handleActivityFeedClick={handleClick}
              activityFeedProps={activityFeedBtnProps}
            />
          )}
        </WrapperComponent>
      </>
    );
  }, [
    rightPanelView,
    mapRef,
    onOpen,
    isMobile,
    isUnverfiedUserLoggedIn,
    googleTranslationEnabled
  ]);

  useEffect(() => {
    const handleKeyDown: any = (event: KeyboardEvent) => {
      if (
        event.altKey &&
        event.shiftKey &&
        event.code === "KeyN" &&
        accessStatus
      ) {
        handlePinClick();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handlePinClick]);

  return (
    <>
      {iconComponent()}
      <UnsavedChangesModal />
    </>
  );
};

export default Interactions;
